import { Injectable } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, pairwise, startWith } from "rxjs";
import { SearchService } from "./search.service";
import { FieldsListsService } from "./fields-lists.service";
import { PAGE_ID, DEFAULT_PAGE_ID, DEFAULT_ENTITY_INDEX, MENU_CATEGORIES, ENTITY_INDEX, TAB_ID, ENTITY_ID, ENTITY_TYPE, PAGES, INFO_PAGES } from "../constants";
import { TabId } from "../enums/tab-id.enum";
import { CategoryData } from "../interfaces/categoryData";
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { PageItem } from "../components/module-header/header-interfaces";


@Injectable({
    providedIn: 'root'
    
  })
  
  export class RoutingService {
    currentParams!: ParamMap;
    previousParams!: ParamMap;
    private activePageSubject = new BehaviorSubject<PageItem>(PAGES[0]);
    activePage$ = this.activePageSubject.asObservable();
    private activeMenuCategorySubject = new BehaviorSubject<CategoryData>(MENU_CATEGORIES[0]);
    activeMenuCategory$ = this.activeMenuCategorySubject.asObservable();
    private activeTabIndexSubject = new BehaviorSubject<number>(TabId.DEFAULT);
    activeTabIndex$ = this.activeTabIndexSubject.asObservable();
    private entityIndexSubject = new BehaviorSubject<number>(DEFAULT_ENTITY_INDEX);
    entityIndex$ = this.entityIndexSubject.asObservable();
    private entityDetailsSubject = new BehaviorSubject<{ type: string, id: number }>({ type: '', id: 0 });
    entityDetails$ = this.entityDetailsSubject.asObservable();
    shouldSkipSearch = false;
    constructor(
      private route: ActivatedRoute,
      private router: Router,
      private searchService: SearchService,
      private fieldsListsService: FieldsListsService
      ,protected $gaService: GoogleAnalyticsService      
    ) {

      combineLatest([
        this.route.queryParamMap.pipe(
          startWith(this.route.snapshot.queryParamMap), pairwise()),
        this.fieldsListsService.allFieldsLoaded$,
      ]).subscribe(([[previousParams, currentParams]]) => {
        this.populateValuesFromParams(previousParams, currentParams);
      });
    }

    populateValuesFromParams(previousParams: ParamMap, currentParams: ParamMap): void { 
        const pageId = currentParams.get(PAGE_ID) ?? DEFAULT_PAGE_ID;
        const tabId = currentParams.has(TAB_ID) ? Number(currentParams.get(TAB_ID)) : Number(TabId.DEFAULT);
        const entityIndex = Number(currentParams.get(ENTITY_INDEX)) || DEFAULT_ENTITY_INDEX;
        const entityId = Number(currentParams.get(ENTITY_ID)) || 0;
        const entityType = currentParams.get(ENTITY_TYPE) ?? '';
        this.shouldSkipSearch = this.shouldSkipSearch || !!entityType;
        this.activePageSubject.next([...PAGES, ...INFO_PAGES].find(c => c.id.toString() === pageId) ?? PAGES[0]);
        this.activeMenuCategorySubject.next(MENU_CATEGORIES.find(c => c.id.toString() === pageId) ?? MENU_CATEGORIES[0]);
        this.activeTabIndexSubject.next(tabId);
        this.entityIndexSubject.next(entityIndex);
        this.entityDetailsSubject.next({ type: entityType, id: entityId });
        this.searchService.initSearchParams(currentParams, previousParams, this.shouldSkipSearch);
        this.shouldSkipSearch = false;
    }

    updateSelectedPage(page: PageItem): void {
      this.activePageSubject.next(page);
      this.navigateWithMerge({[PAGE_ID]: page.id});
    }
    
    
    updateSelectedMenuCategory(index: number): void {      
        this.activeMenuCategorySubject.next(MENU_CATEGORIES[index]);
        this.navigateWithMerge({[PAGE_ID]: index});
    }

    updateActiveTabIndex(index: number): void {
        this.activeTabIndexSubject.next(index);
        this.navigateWithMerge({[TAB_ID]: index, [ENTITY_TYPE]: undefined, [ENTITY_ID]: undefined});
    }

    updateEntityIndex(index: number): void {
        this.entityIndexSubject.next(index);
        this.navigateWithMerge({[ENTITY_INDEX]: index});
    }
    updateEntityId(id: number): void {
      this.navigateWithMerge({[ENTITY_ID]: id});
      const currentEntityDetails = this.entityDetailsSubject.getValue();
      this.entityDetailsSubject.next({ ...currentEntityDetails, id: Number(id)});
    }
    resetEntityDetails(): void {
        this.entityDetailsSubject.next({ type: '', id: 0 });
        this.navigateWithMerge({[ENTITY_TYPE]: undefined, [ENTITY_ID]: undefined});
    }

    navigateMap(entityId: number){
      this.activeTabIndexSubject.next(TabId.MAP);
      this.entityDetailsSubject.next({ type: '', id: entityId });
      this.navigateWithMerge({[TAB_ID]: TabId.MAP, [ENTITY_ID]: entityId});
    }
    navigateTable(entityIndex: number){
      this.activeTabIndexSubject.next(TabId.TABLE);
      this.entityIndexSubject.next(entityIndex);
      this.entityDetailsSubject.next({ type: '', id: 0 });
      this.navigateWithMerge({
        [TAB_ID]: TabId.TABLE, 
        [ENTITY_INDEX]: entityIndex,
        [ENTITY_TYPE]: undefined,
        [ENTITY_ID]: undefined});
    }

    navigateToEntity(entityId: number, entityType: string): void {
      this.entityDetailsSubject.next({ type: entityType, id: entityId });
      this.navigateWithMerge({
        [ENTITY_TYPE]: entityType,
        [ENTITY_ID]: entityId
      });
    }
  
    navigateWithMerge(queryParams: any, url?: string[]): void {
      this.router.navigate(url || [], { queryParams, queryParamsHandling: 'merge' });
    }

    navigateWithReplace(queryParams: any, url?: string[]): void {
      this.router.navigate(url || [], { queryParams });
    }

    /**
     * Navigate from a list row on an entity page to that entity (e.g. manuscript -> fragment).
     * Like navigateWithMerge, plus the clicked row is remembered on the current page's own history entry,
     * so going back (the header's back arrow or the browser's) reopens the list and focuses that row.
     * returnKey is 'section:id' and must match the row's [appReturnFocus].
     */
    navigateToChild(queryParams: any, returnKey: string): void {
      history.replaceState({ ...history.state, returnKey }, '');
      this.navigateWithMerge(queryParams, ['']);
    }

    /** The list section to reopen when returning to this page, if any. */
    getReturnSection(): string | null {
      const returnKey: string | undefined = history.state?.returnKey;
      return returnKey ? returnKey.split(':')[0] : null;
    }

    /** True once if returnKey is the row we left from; clears it so it is not refocused again. */
    consumeReturnKey(returnKey: string): boolean {
      if (history.state?.returnKey !== returnKey) {
        return false;
      }
      const { returnKey: _, ...state } = history.state;
      history.replaceState(state, '');
      return true;
    }

  }